import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './dashShared.css';
import './LegalMain.css';

import { ManageLegal } from './Legal_Elements/ManageLegal';
import { LegalInsights } from './Legal_Elements/LegalInsights';
import { LegalAging } from './Legal_Elements/LegalAging';
import { LegalAISummaryTab } from './Legal_Elements/LegalAISummary';
import { DashboardAIChat } from '../components/DashboardAIChat/DashboardAIChat';
import { DevOverlay } from '../components/DevOverlay/DevOverlay';
import { DashboardLoader } from '../components/DashboardLoader/DashboardLoader';
import { API_URL } from '../config/api';
import { readApiJson, PERMISSION_DENIED_MESSAGE } from '../utils/apiErrors';

// Checkbox-dropdown multi-select — lets a filter like Current Status match
// several values at once (e.g. "NA" and "In Progress" together), which a
// plain <select> can't express. Same interaction shape as the Pipeline
// dashboards' week-filter dropdown (click the button, check boxes, click
// outside to close) but sends its selection to the backend as a filter
// instead of filtering already-fetched data client-side.
//
// The panel renders through a portal into document.body, positioned with
// `position: fixed` from the trigger button's own bounding rect, instead of
// living inside .legal-filter-controls like a normal absolutely-positioned
// dropdown would — that row is a deliberate single-line horizontal-scroll
// container (`overflow-x: auto`), and CSS computes overflow-y as auto too
// once overflow-x isn't visible, so an in-flow dropdown panel taller than
// the row gets silently clipped at the row's bottom edge instead of
// floating over the rest of the page. The portal sidesteps that entirely.
function MultiSelectFilter({ label, options, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    const updateRect = useCallback(() => {
        if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect());
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        updateRect();
        const close = (e) => {
            if (wrapRef.current && wrapRef.current.contains(e.target)) return;
            if (panelRef.current && panelRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', close);
        // The trigger can move (horizontal scroll inside the filter row, a
        // window resize, or the sticky filter bar's own vertical scroll) —
        // keep the floating panel glued to it instead of drifting away.
        window.addEventListener('scroll', updateRect, true);
        window.addEventListener('resize', updateRect);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', updateRect, true);
            window.removeEventListener('resize', updateRect);
        };
    }, [open, updateRect]);

    const toggle = (value) => {
        onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    };
    const summary = selected.length ? `${selected.length} selected` : 'All';
    return (
        <label className={`dash-filter-group${selected.length ? ' active' : ''}`}>
            <span className="dash-filter-label">{label}</span>
            <div className="legal-ms" ref={wrapRef}>
                <button type="button" className="legal-ms-btn dash-filter-select" onClick={() => setOpen((o) => !o)}>
                    {summary}
                </button>
                {open && rect && createPortal(
                    <div
                        className="legal-ms-panel legal-ms-panel--floating"
                        role="listbox" aria-multiselectable="true"
                        ref={panelRef}
                        style={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, minWidth: rect.width }}
                    >
                        {options.map((o) => (
                            <label key={o} className="legal-ms-option">
                                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                                {o}
                            </label>
                        ))}
                        {!options.length && <div className="legal-ms-empty">No options</div>}
                    </div>,
                    document.body,
                )}
            </div>
        </label>
    );
}

const BASE_TABS = [
    { id: 'insights', label: 'Legal Insights', dot: '#0891b2', component: 'LegalInsights' },
    { id: 'aging', label: 'Aging', dot: '#ea580c', component: 'LegalAging' },
    { id: 'ai-summary', label: 'AI Legal Summary', dot: '#6366f1', component: 'LegalAISummaryTab' },
    { id: 'manage', label: 'Manage Legal Master', dot: '#7c3aed', component: 'ManageLegal' },
];

// Document Category / Sub-Category are handled separately below (dependent
// dropdowns sourced from the LegalCategory/LegalSubCategory master list, not
// this generic loop), so they're deliberately not in this list.
const FILTER_KEYS = [
    ['country', 'Country'],
    ['legal_status', 'Legal Status'],
    ['finance_status', 'Finance Status'],
    ['kosmos_signatory_status', '1Kosmos Signatory Status'],
    ['partner_signatory_status', 'Partner Signatory Status'],
    ['current_status', 'Current Status'],
    ['saved_on_relativity', 'Saved on Relativity'],
];

function LegalDashboard({ user, onDataLoaded }) {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState('insights');
    const [filters, setFilters] = useState({});
    const [filtersExpanded, setFiltersExpanded] = useState(true);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const fileRef = useRef(null);
    const canManageData = user?.isSuperuser || user?.access?.includes('data_manager');

    // Master category list — drives the dependent Document Category / Sub-
    // Category filters below. Sourced from Manage Categories rather than
    // `data.filters.document_*` (which only lists values already present on
    // some record) so a newly-defined category shows up in the filter right
    // away, before any document uses it yet.
    const [masterCategories, setMasterCategories] = useState([]);
    const loadCategories = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/legal/categories/`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load categories.');
            setMasterCategories(body.categories || []);
        } catch {
            // Filters just fall back to empty option lists.
        }
    }, []);
    useEffect(() => { loadCategories(); }, [loadCategories]);
    const selectedFilterCategory = masterCategories.find(c => c.name === filters.document_category);

    const query = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (Array.isArray(v)) { if (v.length) params.set(k, v.join(',')); }
            else if (v) params.set(k, v);
        });
        if (search) params.set('q', search);
        return params.toString();
    }, [filters, search]);

    // `silent` skips the setLoading(true)/false bracket — the full-page
    // loading state gates the whole `dash-content` tree below (see the
    // `!loading && data?.has_data` check), so toggling it unmounts whatever's
    // currently rendered there, including any open popup's local state (e.g.
    // the Status Timeline modal, which lives inside ManageLegal/LegalAging/
    // LegalInsights). A background refresh after saving a status update
    // needs new KPI numbers, not a screen-blanking reload, so it must not
    // touch `loading` or it closes the very popup that triggered it.
    const loadDashboard = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/legal/dashboard/${query ? `?${query}` : ''}`, { credentials: 'include' });
            setData(await readApiJson(res, 'The Legal dashboard API could not be loaded.'));
            onDataLoaded?.();
        } catch (err) {
            setMessage(err.message === PERMISSION_DENIED_MESSAGE ? err.message : `${err.message} Check that Django is running on port 8000.`);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [query, onDataLoaded]);

    const refreshDashboard = useCallback(() => loadDashboard({ silent: true }), [loadDashboard]);

    useEffect(() => { loadDashboard(); }, [loadDashboard]);

    const uploadFile = async (file, mode = 'replace') => {
        if (!file || !canManageData) return;
        setUploading(true); setMessage('');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('mode', mode);
        try {
            const res = await fetch(`${API_URL}/api/legal/import/`, { method: 'POST', body: fd, credentials: 'include' });
            const result = await readApiJson(res, 'Legal import failed.');
            if (mode === 'replace') setFilters({});
            setMessage(result.message);
            await loadDashboard();
        } catch (err) {
            setMessage(err.message);
        } finally {
            setUploading(false);
        }
    };

    const saveRecord = async (payload) => {
        const res = await fetch(`${API_URL}/api/legal/save/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
        });
        const result = await readApiJson(res, 'Could not save the record.');
        await refreshDashboard();
        return result;
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (f) uploadFile(f);
    };

    // Array-valued (multi-select) filters are "active" when non-empty, not
    // merely truthy — an empty array is still truthy in JS and would
    // otherwise count as an applied filter.
    const isActiveFilter = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));
    const hasFilters = Object.values(filters).some(isActiveFilter) || Boolean(search);
    const activeFilterCount = Object.values(filters).filter(isActiveFilter).length + (search ? 1 : 0);

    return (
        <div className="dash-shell legal-dashboard">

            {/* ── Sub-navigation ── */}
            <DevOverlay name="TabNavigation">
                <div className="dash-subnav">
                    <div className="dash-tabs">
                        {BASE_TABS.map(t => (
                            <button
                                key={t.id}
                                data-component={t.component}
                                className={`dash-tab${tab === t.id ? ' active' : ''}`}
                                onClick={() => setTab(t.id)}
                            >
                                <span
                                    className="dash-tab-dot"
                                    style={{ background: tab === t.id ? t.dot : 'transparent', border: `2px solid ${t.dot}` }}
                                />
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="legal-filter-toggle legal-tab-filter-toggle"
                        aria-expanded={filtersExpanded}
                        onClick={() => setFiltersExpanded(expanded => !expanded)}
                    >
                        {filtersExpanded ? 'Collapse Filters' : 'Expand Filters'}
                        <svg className={filtersExpanded ? 'expanded' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    <input ref={fileRef} type="file" accept=".xlsx" onChange={handleFileChange} style={{ display: 'none' }} />
                </div>
            </DevOverlay>

            {/* ── Filter bar ── */}
            {data?.has_data && (
                <div className="legal-sticky-filters">
                    <DevOverlay name="FilterBar">
                        {filtersExpanded && <div className="dash-filterbar legal-filter-panel">
                            <div className="legal-filter-controls">
                                <div className={`legal-search-wrap${search ? ' active' : ''}`}>
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="20" y1="20" x2="16.4" y2="16.4"/></svg>
                                    <input
                                        className="legal-search"
                                        placeholder="Search entity, category, requestor…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    {activeFilterCount > 0 && <span className="legal-active-count">{activeFilterCount} active</span>}
                                    {hasFilters && (
                                        <button className="dash-filter-reset" onClick={() => { setFilters({}); setSearch(''); }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            Reset
                                        </button>
                                    )}
                                </div>
                                <div className="legal-filter-grid">
                                    <label className={`dash-filter-group${filters.document_category ? ' active' : ''}`}>
                                        <span className="dash-filter-label">Document Category</span>
                                        <select
                                            className="dash-filter-select"
                                            value={filters.document_category || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setFilters((f) => ({ ...f, document_category: value, document_subcategory: '' }));
                                            }}
                                        >
                                            <option value="">All</option>
                                            {masterCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </label>
                                    <label className={`dash-filter-group${filters.document_subcategory ? ' active' : ''}`}>
                                        <span className="dash-filter-label">Document Sub-Category</span>
                                        <select
                                            className="dash-filter-select"
                                            value={filters.document_subcategory || ''}
                                            onChange={(e) => setFilters((f) => ({ ...f, document_subcategory: e.target.value }))}
                                            disabled={!selectedFilterCategory}
                                        >
                                            <option value="">All</option>
                                            {(selectedFilterCategory?.subcategories || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                                        </select>
                                    </label>
                                    {FILTER_KEYS.map(([key, label]) => (
                                        <MultiSelectFilter
                                            key={key}
                                            label={label}
                                            options={data.filters[key] || []}
                                            selected={filters[key] || []}
                                            onChange={(values) => setFilters((f) => ({ ...f, [key]: values }))}
                                        />
                                    ))}
                                    <label className={`dash-filter-group${filters.date_range ? ' active' : ''}`}>
                                        <span className="dash-filter-label">Date Range</span>
                                        <select
                                            className="dash-filter-select"
                                            value={filters.date_range || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setFilters((f) => ({ ...f, date_range: value, date_from: '', date_to: '' }));
                                            }}
                                        >
                                            <option value="today">Today</option>
                                            <option value="week">This Week</option>
                                            <option value="month">This Month</option>
                                            <option value="quarter">This Quarter</option>
                                            <option value="year">This Year</option>
                                            <option value="custom">Custom</option>
                                            <option value="">All</option>
                                        </select>
                                    </label>
                                    {filters.date_range === 'custom' && (
                                        <>
                                            <label className={`dash-filter-group${filters.date_from ? ' active' : ''}`}>
                                                <span className="dash-filter-label">Start Date</span>
                                                <input
                                                    type="date"
                                                    className="dash-filter-select"
                                                    value={filters.date_from || ''}
                                                    onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                                                />
                                            </label>
                                            <label className={`dash-filter-group${filters.date_to ? ' active' : ''}`}>
                                                <span className="dash-filter-label">End Date</span>
                                                <input
                                                    type="date"
                                                    className="dash-filter-select"
                                                    value={filters.date_to || ''}
                                                    onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                                                />
                                            </label>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>}
                    </DevOverlay>
                </div>
            )}

            {/* ── Messages ── */}
            {message && <div className="dash-notice">{message}</div>}

            {/* ── Loading ── */}
            {loading && (
                <div className="dash-loading">
                    <DashboardLoader label="Preparing Legal dashboard..." />
                    Loading the document register…
                </div>
            )}

            {/* ── Empty state ── */}
            {!loading && data && !data.has_data && (
                <div className="dash-empty">
                    <div className="dash-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    </div>
                    <h2>Legal Dashboard Ready</h2>
                    <p>{canManageData ? (data.message || 'Upload the Legal Dashboard workbook to get started.') : 'No Legal data has been loaded. Please contact a Data Manager.'}</p>
                    {canManageData && <button className="dash-empty-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        {uploading ? 'Importing…' : 'Upload Legal Dashboard Workbook'}
                    </button>}
                </div>
            )}

            {/* ── AI chat assistant — same floating launcher + chat panel every
                 other dashboard (ARR/AR/Pipeline) already has, answering
                 free-form questions about this dashboard's own data. The
                 "since your last login" canned summary that used to live
                 behind this same circle now has its own "AI Legal Summary"
                 tab instead (see BASE_TABS above). ── */}
            {!loading && data?.has_data && (
                <DashboardAIChat dashboard="legal" activeTab={tab} filters={filters} />
            )}

            {/* ── Content ── */}
            {!loading && data?.has_data && (
                <div className="dash-content">
                    {tab === 'manage' && (
                        <DevOverlay name="ManageLegal">
                            <ManageLegal
                                data={data}
                                upload={uploadFile}
                                save={saveRecord}
                                uploading={uploading}
                                canManageData={canManageData}
                                onCategoriesChanged={loadCategories}
                                onStatusChanged={refreshDashboard}
                            />
                        </DevOverlay>
                    )}
                    {tab === 'insights' && (
                        <DevOverlay name="LegalInsights">
                            <LegalInsights data={data} searchQuery={search} canManageData={canManageData} onStatusChanged={refreshDashboard} />
                        </DevOverlay>
                    )}
                    {tab === 'aging' && (
                        <DevOverlay name="LegalAging">
                            <LegalAging data={data} canManageData={canManageData} onStatusChanged={refreshDashboard} />
                        </DevOverlay>
                    )}
                    {tab === 'ai-summary' && (
                        <DevOverlay name="LegalAISummaryTab">
                            <LegalAISummaryTab />
                        </DevOverlay>
                    )}
                </div>
            )}
        </div>
    );
}

export default LegalDashboard;
